
const SAMPLING = {
  QUICK_MS: 3000, // 3s quick sample - try this first
  EXTENDED_MS: 7000, // 7s extended sample - fallback for slow Android GPS
  TARGET_GOOD_ACCURACY: 100, // if quick sample worse than this, offer extended
  MIN_FRESH_MS: 5000, // drop samples older than this
  FALLBACK_MAX_AGE_MS: 20000 // only use cached quick fix if newer than this
}

// ========== Quality Thresholds ==========
// const QUALITY = {
//   MAX_ACCURACY: 210, // reject fixes above this
//   MAX_JUMP_M: 20000, // suspicious if jump > 20 km
//   MAX_SPEED_KMH: 180, // suspicious if implied speed >180 km/h
//   MAX_IDENTICAL_ZERO_FIXES: 3 // suspicious repeated perfect identical fixes
// }
export default {
  getSimpleLocation () {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by your browser'))
      }

      // Try fast first (5s), fallback to extended (15s) if it fails
      const quickOpts = {
        enableHighAccuracy: false,
        maximumAge: 0,
        timeout: SAMPLING.QUICK_MS
      }

      navigator.geolocation.getCurrentPosition(
        quickPos => {
          resolve(quickPos)
        },
        () => {
          // Fast attempt failed, try with extended timeout (Android needs this)
          const extendedOpts = {
            enableHighAccuracy: false,
            maximumAge: 0,
            timeout: SAMPLING.EXTENDED_MS
          }
          navigator.geolocation.getCurrentPosition(
            slowPos => {
              resolve(slowPos)
            },
            finalErr => { reject(finalErr) },
            extendedOpts
          )
        },
        quickOpts
      )
    })
  },
  getGoodFix ({ windowMs = 10000, minFreshMs = null, minSamples = 3, updateCallback = null } = {}) {
    // Auto-set minFreshMs to match windowMs if not specified
    if (minFreshMs === null) {
      minFreshMs = windowMs
    }
    return new Promise((resolve, reject) => {
      if (!('geolocation' in navigator)) {
        return reject(new Error('Geolocation API unavailable'))
      }

      const opts = { enableHighAccuracy: true, maximumAge: 0, timeout: windowMs }
      const samples = []
      const sampleHistory = [] // track all considered samples (accepted after filters)
      let done = false
      let watchId = null
      let windowTimer = null

      const takeSample = (pos) => {
        const age = Date.now() - pos.timestamp
        const fresh = age <= minFreshMs
        const acc = pos.coords.accuracy
        if (!fresh || !Number.isFinite(acc)) return
        // if (acc > QUALITY.MAX_ACCURACY) {
        //   // log(`Improved: discard sample accuracy ${Math.round(acc)} m (> ${QUALITY.MAX_ACCURACY} m)`)
        //   return
        // }

        updateCallback && updateCallback(pos)
        sampleHistory.push(pos)
        samples.push(pos)

        if (acc <= SAMPLING.TARGET_GOOD_ACCURACY) {
          finalize(pos)
        }
      }

      const handleError = (e) => {
        if (!done) {
          done = true
          if (watchId !== null) navigator.geolocation.clearWatch(watchId)
          if (windowTimer !== null) clearTimeout(windowTimer)
          reject(new Error(this.getErrorMessage(e)))
        }
      }

      // Once we hit a sample with the desired accuracy we can stop watching further fixes
      const finalize = (candidate) => {
        if (done) return
        done = true
        if (watchId !== null) navigator.geolocation.clearWatch(watchId)
        if (windowTimer !== null) clearTimeout(windowTimer)
        updateCallback && updateCallback(candidate, true)
        resolve(candidate)
      }

      // Get initial fix
      navigator.geolocation.getCurrentPosition(takeSample, handleError, opts)

      // Watch for more samples
      watchId = navigator.geolocation.watchPosition(takeSample, () => {
        // Ignore watch errors, we'll handle timeout
      }, opts)

      // Process samples after window
      windowTimer = setTimeout(() => {
        if (done) return

        if (watchId !== null) navigator.geolocation.clearWatch(watchId)

        if (!samples.length) return reject(new Error('No valid samples (all rejected or none fresh)'))
        samples.sort((a, b) => a.coords.accuracy - b.coords.accuracy)
        const bestGroup = samples.slice(0, Math.max(minSamples, Math.ceil(samples.length / 2)))
        const pick = bestGroup[Math.floor(bestGroup.length / 2)]
        // if (pick.coords.accuracy > QUALITY.MAX_ACCURACY) {
        //   return reject(new Error(`Best candidate accuracy ${Math.round(pick.coords.accuracy)} m exceeds ${QUALITY.MAX_ACCURACY} m (all samples poor)`))
        // }
        finalize(pick)
      }, windowMs)
    })
  },

  /**
   * Determines distance in meters between user location and center of site, using Haversine formula
   * @param {Object} user the user location object with latitude and longitude properties
   * @param {Object} center the site center location object with latitude and longitude properties
   * @returns distance in meters, or Infinity if inputs are invalid
   */
  distanceToSite (user, center) {
    // Haversine formula
    const toRad = deg => deg * Math.PI / 180
    const R = 6371000
    const dLat = toRad(center.latitude - user.latitude)
    const dLon = toRad(center.longitude - user.longitude)
    const lat1 = toRad(user.latitude)
    const lat2 = toRad(center.latitude)
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(lat1) * Math.cos(lat2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    const d = R * c

    return d
  },

  getErrorMessage (error) {
    switch (error.code) {
      case 1:
        return 'Location permission denied. Please enable location services.'
      case 2:
        return 'Location unavailable. Please check your device settings.'
      case 3:
        return 'Location request timed out. Please try again.'
      default:
        return 'Unable to retrieve location.'
    }
  }
}
